import { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  Stack,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import PsychologyIcon from '@mui/icons-material/Psychology';
import DownloadIcon from '@mui/icons-material/Download';
import AudioRecorder from '../components/AudioRecorder';
import AnimalInfoForm from '../components/AnimalInfoForm';
import ReportCard from '../components/ReportCard';
import TokenUsageInfo from '../components/TokenUsageInfo';
import StatusAlert from '../components/StatusAlert';
import {
  getHealth,
  getAppointment,
  transcribeAudio,
  extractInfo,
  analyze,
  refineAnalysis,
  finalizeConclusion,
  requestHypothesisTreatment,
  saveAppointment,
  updateAppointment,
} from '../api/client';
import type { AnimalInfo, AnalysisResult, Appointment, ClarificationQuestion, Hypothesis, Usage } from '../types';
import type { VoicePatientPatch } from '../components/voiceClient';
import { useNavigate, useParams } from 'react-router-dom';

const initialAnimalInfo: AnimalInfo = {
  кличка: '',
  вид: '',
  порода: '',
  возраст: '',
  пол: '',
  вес: '',
  жалобы: '',
};

export default function HomePage() {
  const navigate = useNavigate();
  const { appointmentId: appointmentIdParam } = useParams();
  const [status, setStatus] = useState<{ message: string; type: 'info' | 'success' | 'warning' | 'error' } | null>(null);
  const [llmReady, setLlmReady] = useState<boolean | null>(null);
  const [transcript, setTranscript] = useState('');
  const [partialTranscript, setPartialTranscript] = useState('');
  const [animalInfo, setAnimalInfo] = useState<AnimalInfo>(initialAnimalInfo);
  const [reports, setReports] = useState<AnalysisResult[]>([]);
  const [totalUsage, setTotalUsage] = useState<Usage>({ tokens_in: 0, tokens_out: 0, cost: 0, model: '' });
  const [appointmentId, setAppointmentId] = useState<number | null>(null);
  const [refineText, setRefineText] = useState('');
  const [loading, setLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [loadingTreatmentIndex, setLoadingTreatmentIndex] = useState<number | null>(null);
  const [clarificationRequest, setClarificationRequest] = useState<{
    questions: ClarificationQuestion[];
    answers: Record<string, string>;
    hypothesis: Hypothesis;
    mode: 'finalize' | 'treatment';
    hypothesisIndex?: number;
  } | null>(null);

  useEffect(() => {
    getHealth()
      .then((data) => {
        setLlmReady(data.llm_ready);
        if (!data.transcription_ready) {
          setStatus({ message: 'Для распознавания задайте OPENAI_API_KEY или OPENAI_STT_API_KEY.', type: 'warning' });
        } else if (data.llm_ready) {
          setStatus({ message: 'Готов к работе: распознавание через OpenRouter и анализ через LLM настроены.', type: 'success' });
        } else {
          setStatus({ message: 'OpenRouter STT настроен, но LLM-анализ недоступен (проверьте OPENAI_API_KEY).', type: 'warning' });
        }
      })
      .catch(() => {
        setStatus({ message: 'Не удалось связаться с сервером.', type: 'error' });
      });
  }, []);

  useEffect(() => {
    if (!appointmentIdParam) {
      setHistoryLoaded(false);
      return;
    }
    const id = Number(appointmentIdParam);
    if (!Number.isInteger(id) || id <= 0) {
      setStatus({ message: 'Некорректный номер приёма.', type: 'error' });
      return;
    }
    setLoading(true);
    getAppointment(id)
      .then((item) => {
        setAppointmentId(item.id);
        setTranscript(item.transcript || '');
        setPartialTranscript('');
        setAnimalInfo({ ...initialAnimalInfo, ...(item.animal_info || {}) });
        setReports((item.reports || []) as AnalysisResult[]);
        setTotalUsage({
          tokens_in: item.tokens_in || 0,
          tokens_out: item.tokens_out || 0,
          cost: item.cost || 0,
          model: item.model || '',
        });
        setRefineText('');
        setHistoryLoaded(true);
        setStatus({ message: `Приём №${item.id} загружен из истории.`, type: 'success' });
      })
      .catch((err: any) => setStatus({ message: 'Ошибка загрузки приёма: ' + err.message, type: 'error' }))
      .finally(() => setLoading(false));
  }, [appointmentIdParam]);

  useEffect(() => {
    if (!historyLoaded || !appointmentId) return;
    const timer = window.setTimeout(() => {
      void updateAppointment(appointmentId, {
        transcript,
        animal_info: animalInfo,
        reports,
        model: totalUsage.model,
        tokens_in: totalUsage.tokens_in,
        tokens_out: totalUsage.tokens_out,
        cost: totalUsage.cost,
      }).catch((err) => console.error('Ошибка автосохранения продолженного приёма:', err));
    }, 800);
    return () => window.clearTimeout(timer);
  }, [historyLoaded, appointmentId, transcript, animalInfo, reports, totalUsage]);

  const handleAudioReady = useCallback(
    async (blob: Blob, filename: string) => {
      setLoading(true);
      setStatus({ message: 'Распознаём речь...', type: 'info' });
      try {
        const file = new File([blob], filename, { type: 'audio/wav' });
        const { transcript: t } = await transcribeAudio(file);
        setTranscript(t);
        setStatus({ message: 'Распознавание завершено.', type: 'success' });

        if (t.trim()) {
          try {
            const info = await extractInfo(t);
            setAnimalInfo((prev) => ({ ...prev, ...info }));
            setStatus({ message: 'Данные пациента заполнены автоматически.', type: 'success' });
          } catch (err: any) {
            setStatus({ message: 'Автозаполнение не сработало: ' + err.message, type: 'warning' });
          }
        }
      } catch (err: any) {
        setStatus({ message: 'Ошибка распознавания: ' + err.message, type: 'error' });
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const handleFinalTranscript = useCallback((text: string) => {
    setTranscript((current) => [current.trim(), text.trim()].filter(Boolean).join(' '));
    setPartialTranscript('');
    setStatus({ message: 'Фрагмент речи распознан.', type: 'success' });
  }, []);

  const handlePatientPatch = useCallback((patch: VoicePatientPatch) => {
    setAnimalInfo((current) => {
      if (!patch.жалобы) return { ...current, ...patch };
      const complaints = [current.жалобы, patch.жалобы]
        .flatMap((value) => (value ?? '').split(','))
        .map((value) => value.trim())
        .filter(Boolean);
      const uniqueComplaints = [...new Map(complaints.map((value) => [value.toLocaleLowerCase('ru-RU'), value])).values()];
      return { ...current, ...patch, жалобы: uniqueComplaints.join(', ') };
    });
    setStatus({ message: 'Данные пациента обновлены из диктовки.', type: 'success' });
  }, []);

  const handleAnalyze = useCallback(async () => {
    if (!transcript.trim()) return;
    if (!llmReady) {
      setStatus({ message: 'LLM не настроен.', type: 'warning' });
      return;
    }
    setLoading(true);
      setStatus({ message: 'Анализирую анамнез и формирую гипотезы...', type: 'info' });
    try {
      const result = await analyze(transcript, animalInfo);
      result.title = 'Заключение';
      result.conclusion = '';
      result.owner_recommendations = [];
      result.veterinarian_notes = '';
      result.disclaimer = '';
      result.is_final = false;
      const usage = result.usage || { tokens_in: 0, tokens_out: 0, cost: 0, model: '' };
      const nextUsage: Usage = {
        tokens_in: totalUsage.tokens_in + usage.tokens_in,
        tokens_out: totalUsage.tokens_out + usage.tokens_out,
        cost: totalUsage.cost + usage.cost,
        model: usage.model || totalUsage.model,
      };
      const newReports = [result];
      setTotalUsage(nextUsage);
      setReports(newReports);
      await saveCurrentState(newReports, nextUsage);
      setStatus({ message: 'Гипотезы сформированы и сохранены.', type: 'success' });
    } catch (err: any) {
      setStatus({ message: 'Ошибка анализа: ' + err.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [transcript, animalInfo, llmReady, reports, totalUsage]);

  const handleRefine = useCallback(async () => {
    const clinicalReports = reports.filter((report) => !report.is_final);
    if (!transcript.trim() || !refineText.trim() || clinicalReports.length === 0) return;
    setLoading(true);
    setStatus({ message: 'Уточняю заключение...', type: 'info' });
    try {
      const previousReport = clinicalReports[clinicalReports.length - 1];
      const result = await refineAnalysis(transcript, animalInfo, previousReport, refineText);
      result.title = `Анализ после уточнения #${clinicalReports.length}`;
      result.refinement = refineText.trim();
      result.conclusion = '';
      result.owner_recommendations = [];
      result.veterinarian_notes = '';
      result.disclaimer = '';
      result.is_final = false;
      const usage = result.usage || { tokens_in: 0, tokens_out: 0, cost: 0, model: '' };
      const nextUsage: Usage = {
        tokens_in: totalUsage.tokens_in + usage.tokens_in,
        tokens_out: totalUsage.tokens_out + usage.tokens_out,
        cost: totalUsage.cost + usage.cost,
        model: usage.model || totalUsage.model,
      };
      const newReports = [...clinicalReports, result];
      setTotalUsage(nextUsage);
      setReports(newReports);
      await saveCurrentState(newReports, nextUsage);
      setRefineText('');
      setStatus({ message: 'Уточнённое заключение готово.', type: 'success' });
    } catch (err: any) {
      setStatus({ message: 'Ошибка уточнения: ' + err.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [transcript, refineText, animalInfo, reports, totalUsage]);

  const handleFinalize = useCallback(async (hypothesis: Hypothesis) => {
    let clinicalReports = reports.filter((report) => !report.is_final);
    if (!transcript.trim() || clinicalReports.length === 0) return;
    const required: Array<keyof AnimalInfo> = ['вид', 'пол', 'возраст', 'вес'];
    const missing = required.filter((field) => !animalInfo[field]?.trim());
    if (missing.length) {
      setStatus({ message: `Для заключения с дозировками заполните: ${missing.join(', ')}.`, type: 'warning' });
      return;
    }
    setLoading(true);
    setStatus({ message: 'Формирую финальное заключение с учётом всех уточнений...', type: 'info' });
    try {
      let selectedHypothesis = hypothesis;
      let treatmentUsage: Partial<Usage> = {};
      const hasCalculatedMedication = Boolean(hypothesis.medications?.length) && hypothesis.medications!.every((medication) => {
        if (typeof medication === 'string' || !medication.calculated_dose?.trim()) return false;
        const administration = `${medication.route_frequency_duration || ''} ${medication.role || ''} ${medication.purpose || ''}`.toLowerCase();
        const isIntravenousInfusion = /инфуз|внутрив|\biv\b|в\/в/.test(administration);
        return !isIntravenousInfusion || Boolean(
          medication.infusion_volume_calculation?.trim() && medication.infusion_rate_calculation?.trim()
        );
      });
      if (!hasCalculatedMedication) {
        setStatus({ message: 'Сначала рассчитываю полную схему лечения и дозировки...', type: 'info' });
        const treatment = await requestHypothesisTreatment(transcript, animalInfo, hypothesis);
        if (treatment.clarification_questions?.length) {
          setClarificationRequest({ questions: treatment.clarification_questions, answers: {}, hypothesis, mode: 'finalize' });
          setStatus({ message: 'Для точного расчёта дозировок нужны дополнительные данные.', type: 'warning' });
          return;
        }
        selectedHypothesis = {
          ...hypothesis,
          treatment_plan: treatment.treatment_plan || [],
          medications: treatment.medications || [],
          excluded_medications: treatment.excluded_medications || [],
        };
        treatmentUsage = treatment.usage || {};
        const latestReportIndex = clinicalReports.length - 1;
        const latestReport = { ...clinicalReports[latestReportIndex] };
        const hypotheses = [...(latestReport.hypotheses || [])];
        const selectedIndex = hypotheses.findIndex((item) => item === hypothesis || (item.name && item.name === hypothesis.name));
        if (selectedIndex >= 0) hypotheses[selectedIndex] = selectedHypothesis;
        latestReport.hypotheses = hypotheses;
        clinicalReports = [...clinicalReports];
        clinicalReports[latestReportIndex] = latestReport;
      }
      setStatus({ message: 'Формирую подробное финальное заключение...', type: 'info' });
      const result = await finalizeConclusion(transcript, animalInfo, clinicalReports, selectedHypothesis);
      result.title = `Финальное заключение: ${selectedHypothesis.name || 'выбранная гипотеза'}`;
      result.is_final = true;
      result.selected_hypothesis = selectedHypothesis.name || '';
      const usage = result.usage || { tokens_in: 0, tokens_out: 0, cost: 0, model: '' };
      const nextUsage: Usage = {
        tokens_in: totalUsage.tokens_in + (treatmentUsage.tokens_in || 0) + usage.tokens_in,
        tokens_out: totalUsage.tokens_out + (treatmentUsage.tokens_out || 0) + usage.tokens_out,
        cost: totalUsage.cost + (treatmentUsage.cost || 0) + usage.cost,
        model: usage.model || treatmentUsage.model || totalUsage.model,
      };
      const newReports = [...clinicalReports, result];
      setTotalUsage(nextUsage);
      setReports(newReports);
      await saveCurrentState(newReports, nextUsage);
      setStatus({ message: 'Финальное заключение готово.', type: 'success' });
    } catch (err: any) {
      setStatus({ message: 'Ошибка финального заключения: ' + err.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [transcript, animalInfo, reports, totalUsage]);

  const handleRequestTreatment = useCallback(async (hypothesis: Hypothesis, hypothesisIndex: number) => {
    const required: Array<keyof AnimalInfo> = ['вид', 'пол', 'возраст', 'вес'];
    const missing = required.filter((field) => !animalInfo[field]?.trim());
    if (missing.length) {
      setStatus({ message: `Для расчёта лечения заполните: ${missing.join(', ')}.`, type: 'warning' });
      return;
    }
    setLoadingTreatmentIndex(hypothesisIndex);
    setStatus({ message: 'Подбираю безопасный вариант лечения и рассчитываю дозировки...', type: 'info' });
    try {
      const treatment = await requestHypothesisTreatment(transcript, animalInfo, hypothesis);
      if (treatment.clarification_questions?.length) {
        setClarificationRequest({
          questions: treatment.clarification_questions,
          answers: {},
          hypothesis,
          mode: 'treatment',
          hypothesisIndex,
        });
        setStatus({ message: 'Для точного расчёта дозировок нужны дополнительные данные.', type: 'warning' });
        return;
      }
      const clinicalIndexes = reports
        .map((report, index) => (!report.is_final ? index : -1))
        .filter((index) => index >= 0);
      const reportIndex = clinicalIndexes[clinicalIndexes.length - 1];
      if (reportIndex === undefined) return;
      const newReports = [...reports];
      const report = { ...newReports[reportIndex] };
      const hypotheses = [...(report.hypotheses || [])];
      hypotheses[hypothesisIndex] = {
        ...hypotheses[hypothesisIndex],
        treatment_plan: treatment.treatment_plan || [],
        medications: treatment.medications || [],
        excluded_medications: treatment.excluded_medications || [],
      };
      report.hypotheses = hypotheses;
      newReports[reportIndex] = report;
      const usage = treatment.usage || {};
      const nextUsage: Usage = {
        tokens_in: totalUsage.tokens_in + (usage.tokens_in || 0),
        tokens_out: totalUsage.tokens_out + (usage.tokens_out || 0),
        cost: totalUsage.cost + (usage.cost || 0),
        model: usage.model || totalUsage.model,
      };
      setReports(newReports);
      setTotalUsage(nextUsage);
      await saveCurrentState(newReports, nextUsage);
      setStatus({ message: 'Лечение и расчёт дозировок сохранены в гипотезе.', type: 'success' });
    } catch (err: any) {
      setStatus({ message: 'Ошибка расчёта лечения: ' + err.message, type: 'error' });
    } finally {
      setLoadingTreatmentIndex(null);
    }
  }, [transcript, animalInfo, reports, totalUsage]);

  const submitDoseClarifications = useCallback(() => {
    if (!clarificationRequest) return;
    const completed = clarificationRequest.questions.every(
      (question) => clarificationRequest.answers[question.key]?.trim()
    );
    if (!completed) {
      setStatus({ message: 'Ответьте на все вопросы, необходимые для расчёта.', type: 'warning' });
      return;
    }
    const request = clarificationRequest;
    const updatedHypothesis: Hypothesis = {
      ...request.hypothesis,
      dose_clarifications: {
        ...(request.hypothesis.dose_clarifications || {}),
        ...request.answers,
      },
    };
    setClarificationRequest(null);
    if (request.mode === 'finalize') void handleFinalize(updatedHypothesis);
    else void handleRequestTreatment(updatedHypothesis, request.hypothesisIndex ?? 0);
  }, [clarificationRequest, handleFinalize, handleRequestTreatment]);

  const saveCurrentState = useCallback(
    async (currentReports: AnalysisResult[], currentUsage: Usage) => {
      const payload: Partial<Appointment> = {
        transcript,
        animal_info: animalInfo,
        reports: currentReports,
        model: currentUsage.model,
        tokens_in: currentUsage.tokens_in,
        tokens_out: currentUsage.tokens_out,
        cost: currentUsage.cost,
      };
      try {
        if (appointmentId) {
          await updateAppointment(appointmentId, payload);
        } else {
          const res = await saveAppointment(payload);
          if (res.id) setAppointmentId(res.id);
        }
      } catch (err) {
        console.error('Ошибка сохранения:', err);
      }
    },
    [transcript, animalInfo, appointmentId]
  );

  const downloadMarkdown = useCallback(() => {
    if (reports.length === 0) return;
    const lastReport = reports[reports.length - 1];
    const lines = ['# Заключение ветеринарного ассистента\n'];
    if (lastReport.anamnesis) lines.push('## Анамнез\n', lastReport.anamnesis + '\n');
    if (lastReport.differential_diagnosis) lines.push('## Дифференциальный диагноз\n', lastReport.differential_diagnosis + '\n');
    if (Array.isArray(lastReport.hypotheses) && lastReport.hypotheses.length) {
      lines.push('## Гипотезы\n');
      lastReport.hypotheses.forEach((h, i) => {
        lines.push(`${i + 1}. **${h.name || 'Гипотеза'}**`);
        lines.push(`   - Обоснование: ${h.reasoning || ''}`);
        lines.push(`   - Вероятность: ${h.probability || ''}`);
        lines.push(`   - Срочность: ${h.urgency || ''}`);
        lines.push(`   - Рекомендуемые исследования: ${h.recommended_tests || ''}\n`);
      });
    }
    if (lastReport.conclusion) lines.push('## Заключение\n', lastReport.conclusion + '\n');
    if (Array.isArray(lastReport.owner_recommendations) && lastReport.owner_recommendations.length) {
      lines.push('## Рекомендации для владельца\n');
      lastReport.owner_recommendations.forEach((r) => lines.push('- ' + r));
      lines.push('');
    }
    if (lastReport.veterinarian_notes) lines.push('## Врачебные заметки\n', lastReport.veterinarian_notes + '\n');
    if (lastReport.disclaimer) lines.push('## Примечание\n', lastReport.disclaimer + '\n');

    const blob = new Blob([lines.join('\n')], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'zaklyuchenie.md';
    a.click();
    URL.revokeObjectURL(url);
  }, [reports]);

  const canAnalyze = transcript.trim().length > 0 && llmReady && !loading;
  const clinicalReports = reports.filter((report) => !report.is_final);
  const currentClinicalReport = clinicalReports[clinicalReports.length - 1];
  const finalReport = [...reports].reverse().find((report) => report.is_final);

  return (
    <Box>
      {status && <StatusAlert message={status.message} type={status.type} />}

      {appointmentIdParam && (
        <Button variant="text" onClick={() => navigate('/history')} sx={{ mb: 2 }}>
          ← Назад к истории
        </Button>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Аудио приёма
          </Typography>
          <AudioRecorder
            onAudioReady={handleAudioReady}
            onFinalTranscript={handleFinalTranscript}
            onPartialTranscript={setPartialTranscript}
            onPatientPatch={handlePatientPatch}
          />
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Данные о пациенте
          </Typography>
          <AnimalInfoForm value={animalInfo} onChange={setAnimalInfo} />
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Расшифровка
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={6}
            value={partialTranscript ? [transcript, partialTranscript].filter(Boolean).join(' ') : transcript}
            onChange={(e) => { setTranscript(e.target.value); setPartialTranscript(''); }}
            placeholder="Здесь появится текст после распознавания. Можно отредактировать вручную."
          />
        </CardContent>
      </Card>

      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<PsychologyIcon />}
          onClick={handleAnalyze}
          disabled={!canAnalyze}
        >
          Проанализировать и выдвинуть гипотезы
        </Button>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={downloadMarkdown}
          disabled={!finalReport}
        >
          Скачать отчёт (.md)
        </Button>
      </Stack>

      {reports.length > 0 && (
        <Box>
          <TokenUsageInfo usage={totalUsage} />
          {currentClinicalReport && (
            <ReportCard
              report={{ ...currentClinicalReport, title: 'Актуальный клинический анализ' }}
              hideFinalSections
              onWriteConclusion={handleFinalize}
              writingConclusion={loading}
              refinements={clinicalReports.slice(1).map((report) => report.refinement || '—')}
              onRequestTreatment={handleRequestTreatment}
              loadingTreatmentIndex={loadingTreatmentIndex}
            />
          )}
        </Box>
      )}

      {reports.length > 0 && (
        <Card sx={{ mt: 3, mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Уточнение заключения
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Добавьте результаты анализов, осмотра, рентгена, УЗИ или другие новые данные. Все гипотезы будут пересмотрены и отсортированы по вероятности.
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={refineText}
              onChange={(e) => setRefineText(e.target.value)}
              placeholder="Например: по УЗИ выявлено утолщение стенки кишечника, общий анализ крови без лейкоцитоза..."
              sx={{ mb: 2 }}
            />
            <Button
              variant="contained"
              onClick={handleRefine}
              disabled={!refineText.trim() || loading}
            >
              Пересмотреть гипотезы и уточнить заключение
            </Button>
          </CardContent>
        </Card>
      )}

      {finalReport && (
        <Box sx={{ mt: 3 }}>
          <ReportCard report={finalReport} finalOnly />
        </Box>
      )}

      {loading && (
        <Box sx={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
          <CircularProgress />
        </Box>
      )}

      <Dialog open={Boolean(clarificationRequest)} onClose={() => setClarificationRequest(null)} fullWidth maxWidth="sm">
        <DialogTitle>Уточните данные для расчёта дозировок</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Без этих данных точный расчёт препаратов или инфузий невозможен.
          </Typography>
          <Stack spacing={2}>
            {clarificationRequest?.questions.map((question) => (
              <TextField
                key={question.key}
                label={question.question}
                helperText={question.reason || ''}
                value={clarificationRequest.answers[question.key] || ''}
                onChange={(event) => setClarificationRequest((current) => current ? {
                  ...current,
                  answers: { ...current.answers, [question.key]: event.target.value },
                } : current)}
                required
                fullWidth
              />
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClarificationRequest(null)}>Отмена</Button>
          <Button variant="contained" onClick={submitDoseClarifications}>Продолжить расчёт</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
